import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Typography,
  Table,
  Space,
  Button,
  Select,
  Input,
  message,
  Tag,
  Tooltip,
  Divider,
  Collapse,
  Alert,
  Col,
  Row,
} from "antd";
import {
  CopyOutlined,
  InfoCircleOutlined,
  CheckOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useGlobal } from "../../contexts/GlobalContext";
import type { ProjectItem } from "../../contexts/GlobalContext";
import SyntaxHighlighter from "react-syntax-highlighter/dist/esm/light";
import js from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import axios from "axios";
import { ToolInfo, ToolListResponse } from "../../types/MCPTypes";

const { Title, Text } = Typography;
const { Option } = Select;

// 注册语言
SyntaxHighlighter.registerLanguage("javascript", js);

interface V3BuiltinToolItem {
  name: string;
  description: string;
  category?: string;
  enabled?: boolean;
}

const builtinToolGroups: Record<string, string[]> = {
  all: ["translate"],
  user: ["translate"],
  project: ["translate", "search", "hotword", "recommend"],
  postchat: ["translate", "search", "hotword", "recommend", "chat"],
  summary: ["translate", "search", "hotword", "recommend", "summary"],
};

const normalizeV3BuiltinTools = (
  tools: V3BuiltinToolItem[],
  mode: string
): ToolListResponse => {
  const allowedNames = new Set(builtinToolGroups[mode] || builtinToolGroups.user);
  const list = tools
    .filter((tool) => allowedNames.has(tool.name))
    .map((tool) => ({
      name: tool.name,
      description: tool.description,
      parameters: [],
      mode,
      disabled: tool.enabled === false,
      input_schema: tool.category ? { category: tool.category } : undefined,
    }));

  return {
    total: list.length,
    list,
  };
};

const MCPServerTab: React.FC = () => {
  const { projects, API_BASE_URL, token } = useGlobal();
  const [tools, setTools] = useState<ToolInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState<string>("user");
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(
    null
  );
  const [copiedItem, setCopiedItem] = useState<string>("");

  const allProjects = projects;
  const fallbackMcpProject =
    selectedProject ||
    allProjects.find((project) => Boolean(project.api_key)) ||
    null;

  // 获取MCP工具列表
  const fetchTools = useCallback(
    async (mode: string) => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await axios.get<{
          code: number;
          msg: string;
          data: V3BuiltinToolItem[];
        }>(`${API_BASE_URL}/api/v3/mcp/tools`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.code === 200) {
          setTools(normalizeV3BuiltinTools(response.data.data || [], mode).list);
        } else {
          message.error(response.data.msg || "获取工具列表失败");
        }
      } catch (error: any) {
        message.error(
          error.response?.data?.msg || "获取工具列表失败，请稍后重试"
        );
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL, token]
  );

  // 当模式或项目变化时获取相应的工具列表
  useEffect(() => {
    let mode = selectedMode;

    // 如果选择了项目，则根据项目类型设置mode
    if (selectedProject) {
      mode = selectedProject.is_postchat ? "postchat" : "summary";
    }

    fetchTools(mode);
  }, [fetchTools, selectedMode, selectedProject]);

  const handleModeChange = (value: string) => {
    setSelectedMode(value);
    setSelectedProject(null);
  };

  // 处理项目变化
  const handleProjectChange = (projectId: number) => {
    const project = projects.find((p) => p.id === projectId);
    if (project) {
      setSelectedProject(project);
    }
  };

  // 复制到剪贴板
  const copyToClipboard = (text: string, item: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(item);
    message.success(`已复制${item}`);
    setTimeout(() => setCopiedItem(""), 3000);
  };

  // 生成MCP服务器链接
  const generateMCPLink = () => {
    if (!fallbackMcpProject?.api_key) {
      return "请先创建可用项目";
    }

    if (selectedProject) {
      return `http://mcp.hm.tianli0.top/mcp/project/${selectedProject.api_key}/${selectedProject.key_value}/sse`;
    } else {
      return `http://mcp.hm.tianli0.top/mcp/all/${fallbackMcpProject.api_key}/sse`;
    }
  };

  // 表格列定义
  const columns = [
    {
      title: "工具名称",
      dataIndex: "name",
      key: "name",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "描述",
      dataIndex: "description",
      key: "description",
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "工具模式",
      dataIndex: "mode",
      key: "mode",
      render: (mode: string) => (
        <Tag
          color={
            mode === "user"
              ? "blue"
              : mode === "project"
              ? "cyan"
              : mode === "postchat"
              ? "green"
              : "purple"
          }
        >
          {mode}
        </Tag>
      ),
    },
    {
      title: "参数数量",
      key: "params",
      render: (record: ToolInfo) => (
        <span>{record.parameters?.length || 0}</span>
      ),
    },
  ];

  // 展开行的渲染
  const expandedRowRender = (record: ToolInfo) => {
    if (!record.parameters || record.parameters.length === 0) {
      return <Alert message="此工具没有参数" type="info" showIcon />;
    }

    const paramColumns = [
      {
        title: "参数名",
        dataIndex: "name",
        key: "name",
        width: 180,
      },
      {
        title: "类型",
        dataIndex: "type",
        key: "type",
        width: 120,
        render: (text: string) => <Tag>{text}</Tag>,
      },
      {
        title: "描述",
        dataIndex: "description",
        key: "description",
      },
      {
        title: "必填",
        dataIndex: "required",
        key: "required",
        width: 80,
        render: (required: boolean) =>
          required ? <Tag color="red">是</Tag> : <Tag color="green">否</Tag>,
      },
      {
        title: "默认值",
        dataIndex: "default",
        key: "default",
        width: 180,
        render: (value: any) =>
          value === null ? (
            <Text type="secondary">无</Text>
          ) : (
            <Text code>{String(value)}</Text>
          ),
      },
    ];

    return (
      <Table
        columns={paramColumns}
        dataSource={record.parameters}
        pagination={false}
        rowKey="name"
        size="small"
        style={{ marginBottom: 16 }}
      />
    );
  };

  return (
    <>
      {/* MCP服务器配置卡片 */}
      <Card
        title={
          <span>
            <i
              className="ri-server-line"
              style={{ marginRight: "8px", color: "#722ED1" }}
            ></i>
            MCP服务器配置
          </span>
        }
        variant="borderless"
        className="shadow-card"
        style={{ marginBottom: 16, borderRadius: 8 }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={5}>选择模式或项目：</Title>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <Text>1. 选择工具模式：</Text>
                    <Select
                      value={selectedMode}
                      onChange={handleModeChange}
                      style={{ width: "100%", marginTop: 8 }}
                      disabled={selectedProject !== null}
                    >
                      <Option value="user">用户模式</Option>
                      <Option value="project">项目模式</Option>
                    </Select>
                  </div>
                </Space>
              </Col>
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <Text>2. 或选择特定项目：</Text>
                    <Select
                      value={selectedProject?.id}
                      onChange={handleProjectChange}
                      style={{ width: "100%", marginTop: 8 }}
                      placeholder="选择项目（选择项目模式时必选，可用工具会有差别）"
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        option?.children
                          ?.toString()
                          .toLowerCase()
                          .includes(input.toLowerCase()) || false
                      }
                    >
                      {allProjects.map((project) => (
                        <Option key={project.id} value={project.id}>
                          {project.name} (
                          {project.is_postchat ? "时长计费" : "Token计费"})
                        </Option>
                      ))}
                    </Select>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block", marginTop: 4 }}
                    >
                      选择项目后，模式将自动切换为项目对应的类型
                    </Text>
                  </div>
                </Space>
              </Col>
            </Row>
          </div>

          <Divider />

          <div>
            <Title level={5} style={{ display: "flex", alignItems: "center" }}>
              <span>MCP服务器链接</span>
              <Tooltip title="此链接用于配置您的MCP服务器，提供给AI使用以访问工具功能">
                <InfoCircleOutlined
                  style={{ marginLeft: 8, fontSize: 14, color: "#722ED1" }}
                />
              </Tooltip>
            </Title>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                value={generateMCPLink()}
                readOnly
                style={{
                  borderRadius: "6px 0 0 6px",
                  height: 40,
                  fontSize: 14,
                  fontFamily: "monospace",
                }}
                prefix={
                  <i
                    className="ri-links-line"
                    style={{ color: "#722ED1", marginRight: 8 }}
                  />
                }
              />
              <Button
                icon={
                  copiedItem === "MCP链接" ? (
                    <CheckOutlined />
                  ) : (
                    <CopyOutlined />
                  )
                }
                onClick={() => copyToClipboard(generateMCPLink(), "MCP链接")}
                style={{
                  height: 40,
                  background: "#722ED1",
                  borderColor: "#722ED1",
                }}
                type="primary"
              >
                {copiedItem === "MCP链接" ? "已复制" : "复制链接"}
              </Button>
              <Button
                icon={<SyncOutlined />}
                onClick={() =>
                  fetchTools(
                    selectedProject
                      ? selectedProject.is_postchat
                        ? "postchat"
                        : "summary"
                      : selectedMode
                  )
                }
                style={{ height: 40 }}
              >
                刷新
              </Button>
            </Space.Compact>

            <Alert
              message="安全提示"
              description={
                <div>
                  <p>
                    MCP服务器链接包含您的API密钥，请勿泄露给不信任的第三方。
                  </p>
                  <p>
                    该链接可用于在AI应用中调用工具，确保在安全的环境中使用。
                  </p>
                  <p>
                    注意，MCP能对您的项目做出修改，请不要通过任何方式对外暴露API及接入相关能力的AI。
                  </p>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          </div>
        </Space>
      </Card>

      {/* 可用工具列表卡片 */}
      <Card
        title={
          <span>
            <i
              className="ri-tools-line"
              style={{ marginRight: "8px", color: "#722ED1" }}
            ></i>
            可用工具列表
          </span>
        }
        variant="borderless"
        className="shadow-card"
        style={{ marginBottom: 16, borderRadius: 8 }}
        extra={
          <Space>
            <Tag color="#722ED1">
              {selectedProject
                ? `${selectedProject.name} (${
                    selectedProject.is_postchat ? "PostChat" : "Summary"
                  }模式)`
                : `${selectedMode}模式`}
            </Tag>
            <Text type="secondary">共 {tools.length} 个工具</Text>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={tools}
          rowKey="name"
          expandable={{
            expandedRowRender,
            expandRowByClick: true,
          }}
          loading={loading}
          pagination={false}
        />

        <Alert
          message="使用说明"
          description={
            <div>
              <p>1. 工具需要通过MCP协议由AI主动调用，您无需手动调用这些工具</p>
              <p>2. 不同模式下可用的工具不同，请根据您的需求选择合适的模式</p>
              <p>3. 项目特定的工具仅在选择对应项目时可用</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Card>

      {/* 使用示例卡片 */}
      <Card
        title={
          <span>
            <i
              className="ri-code-box-line"
              style={{ marginRight: "8px", color: "#722ED1" }}
            ></i>
            使用示例
          </span>
        }
        variant="borderless"
        className="shadow-card"
        style={{ borderRadius: 8 }}
      >
        <Collapse
          defaultActiveKey={["1"]}
          bordered={false}
          items={[
            {
              key: "1",
              label: "快速试用？",
              children: (
                <>
                  <ol style={{ paddingLeft: 24 }}>
                    <li>
                      前往：
                      <a href="https://modelscope.cn/studios/modelscope/mcp-playground">
                        魔搭社区
                      </a>
                    </li>
                    <li>点击配置标签页</li>
                    <li>填入上方的MCP服务器链接</li>
                    <li>开始对话，AI会根据需要自动调用工具</li>
                  </ol>
                  <Alert
                    message="提示：对话中可以尝试以下问题"
                    description={
                      <ul style={{ paddingLeft: 24 }}>
                        <li>现在是几点钟？</li>
                        <li>给我一些博客灵感</li>
                        <li>查看我的项目配置信息</li>
                      </ul>
                    }
                    type="info"
                    showIcon
                  />
                </>
              ),
            },
            {
              key: "2",
              label: "如何在Cursor/Copilot中使用MCP工具",
              children: (
                <ol style={{ paddingLeft: 24 }}>
                  <li>打开Cursor/Copilot</li>
                  <li>进入Cursor设置</li>
                  <li>在模型设置中，添加MCP工具</li>
                  <li>粘贴上方的MCP服务器链接</li>
                  <li>开始对话，AI会根据需要自动调用工具</li>
                </ol>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
};

export default MCPServerTab;
